/**
 * UpgradePrompt Component - Show upgrade options when feature access or limit is exceeded
 * Based on GOVERNANCE_DESIGN_WIREFRAME.md specifications
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, TrendingUp, Zap, CheckCircle } from 'lucide-react';

const UpgradePrompt = ({ 
  feature, 
  limitType,
  currentUsage,
  totalLimit,
  upgradeOptions = [], 
  message, 
  compact = false,
  limitExceeded = false 
}) => {
  const navigate = useNavigate();

  const handleUpgrade = (option) => {
    if (option.type === 'tier_upgrade') {
      navigate(`/subscription/upgrade?tier=${option.tier}`);
    } else if (option.type === 'addon') {
      navigate(`/add-ons/${option.addon_id}`);
    }
  };

  const formatLimitType = (type) => {
    const labels = {
      'business_plans': 'Business Plan',
      'scenarios': 'Scenario',
      'subsidiaries': 'Subsidiary',
      'users': 'Team Member'
    };
    return labels[type] || type;
  };

  // Compact version for inline display
  if (compact) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Lock className="w-5 h-5 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">
              {message || 'Upgrade Required'}
            </span>
          </div>
          {upgradeOptions.length > 0 && (
            <button
              onClick={() => handleUpgrade(upgradeOptions[0])}
              className="text-sm font-medium text-yellow-600 hover:text-yellow-700 underline"
            >
              Upgrade →
            </button>
          )}
        </div>
      </div>
    );
  }

  // Full upgrade prompt for limit exceeded
  if (limitExceeded && limitType) {
    return (
      <div className="bg-white border-2 border-orange-300 rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-orange-100 mb-4">
            <Lock className="h-7 w-7 text-orange-600" />
          </div>
          
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            ⚠️ LIMIT REACHED
          </h3>
          
          <p className="text-gray-700 text-base">
            {message || `You've reached your ${formatLimitType(limitType).toLowerCase()} limit!`}
          </p>
        </div>

        {/* Current Usage Display */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-3">YOUR CURRENT USAGE:</p>
          
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-800">
                {formatLimitType(limitType)}s:
              </span>
              <span className="text-sm font-bold text-gray-900">
                {currentUsage} of {totalLimit === -1 ? '∞' : totalLimit} used
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-orange-500 h-2.5 rounded-full transition-all duration-300"
                style={{ 
                  width: totalLimit === -1 ? '0%' : `${Math.min((currentUsage / totalLimit) * 100, 100)}%` 
                }}
              ></div>
            </div>
          </div>

          <p className="text-xs text-gray-600 mt-3">
            You cannot create more {formatLimitType(limitType).toLowerCase()}s until you:
          </p>
          <ul className="text-xs text-gray-600 mt-1 ml-4">
            <li>• Buy additional capacity, OR</li>
            <li>• Upgrade your subscription, OR</li>
            <li>• Delete an existing item</li>
          </ul>
        </div>

        {/* Upgrade Options */}
        {upgradeOptions.length > 0 && (
          <div className="space-y-4">
            <p className="text-sm font-bold text-gray-800 mb-3">
              RECOMMENDED OPTIONS:
            </p>
            
            {upgradeOptions.map((option, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-300 p-5 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                onClick={() => handleUpgrade(option)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {index === 0 ? (
                        <span className="text-blue-600 font-bold">🎯 OPTION {index + 1}:</span>
                      ) : (
                        <span className="text-indigo-600 font-bold">💡 OPTION {index + 1}:</span>
                      )}
                      <span className="text-sm font-semibold text-gray-700">
                        {option.type === 'tier_upgrade' ? 'Upgrade Plan' : 'Buy Add-on'}
                      </span>
                      {index === 0 && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                          Best Value
                        </span>
                      )}
                    </div>
                    
                    <p className="text-base font-bold text-gray-900 mb-1">
                      {option.tier_display || option.addon_name}
                    </p>
                    
                    {option.type === 'tier_upgrade' && (
                      <div className="text-sm text-gray-700 mt-2 space-y-1">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>
                            {option.tier_limit === -1 
                              ? `Unlimited ${formatLimitType(limitType)}s` 
                              : `${option.tier_limit} ${formatLimitType(limitType)}s`
                            }
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-yellow-600" />
                          <span className="text-xs text-gray-600">Plus all other premium features</span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="text-right ml-4">
                    <p className="text-2xl font-bold text-blue-600 mb-1">
                      ${option.price_monthly || 0}<span className="text-sm font-normal">/mo</span>
                    </p>
                    <button className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1">
                      {index === 0 ? 'Upgrade Now' : 'Learn More'}
                      <TrendingUp className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {upgradeOptions.length === 0 && (
          <div className="text-center mt-4">
            <button
              onClick={() => navigate('/subscription')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              View All Plans
            </button>
          </div>
        )}

        {/* Footer Actions */}
        <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Maybe Later
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => navigate('/pricing')}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Compare All Plans
          </button>
        </div>
      </div>
    );
  }

  // Default feature access prompt
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg shadow-lg p-6">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-blue-100 mb-4">
          <Lock className="h-7 w-7 text-blue-600" />
        </div>
        
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          🔒 Unlock This Feature
        </h3>
        
        <p className="text-gray-700 mb-6 text-base">
          {message || `This feature requires ${feature} access. Upgrade your plan to unlock it.`}
        </p>

        {upgradeOptions.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-800 mb-3">
              YOUR OPTIONS:
            </p>
            
            {upgradeOptions.map((option, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg border-2 border-gray-300 p-5 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                onClick={() => handleUpgrade(option)}
              >
                <div className="flex items-center justify-between">
                  <div className="text-left flex-1">
                    <p className="font-bold text-gray-900 text-base mb-1">
                      {option.type === 'tier_upgrade' ? `Upgrade to ${option.tier_display}` : option.addon_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {option.type === 'tier_upgrade' 
                        ? `Get ${option.tier_limit === -1 ? 'unlimited' : option.tier_limit} ${formatLimitType(limitType)}s + more`
                        : 'Add-on purchase - instant access'}
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-xl font-bold text-blue-600 mb-1">
                      ${option.price_monthly || option.price_per_unit || 0}<span className="text-sm">/mo</span>
                    </p>
                    <button className="text-sm text-blue-600 hover:text-blue-700 font-medium underline">
                      Choose →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {upgradeOptions.length === 0 && (
          <button
            onClick={() => navigate('/subscription')}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            View Plans
          </button>
        )}
      </div>
    </div>
  );
};

export default UpgradePrompt;
